<template>
    <div v-if="!loading" class="mb-4 p-2">
        <div class="row">
            <div class="col card m-2 p-1">
                <div v-if="booking.confirmed_at">
                    <app-button native-type="button" type="danger" @click="confirmBooking(booking.id, false)" small>
                        {{ $t('Unconfirm') }}
                    </app-button>
                    <router-link v-if="!booking.contract && $store.getters.canEditContracts"
                                 :to="{name: 'contracts.create', params: {booking_id: booking.id}}"
                                 class="btn btn-success btn-sm text-white">
                        {{ $t('Create contract') }}
                    </router-link>
                    <router-link v-else-if="booking.contract && $store.getters.canViewContracts"
                                 :to="{name: 'contracts.show', params: {contract_id: booking.contract.id}}"
                                 class="btn btn-info btn-sm ml-2 text-white">
                        {{ $t('Contract') }}
                    </router-link>
                    <router-link :to="{name: 'booking.edit', params: {booking_id: booking.id}}" class="btn btn-primary btn-sm float-right text-white">
                        {{ $t('Edit') }}
                    </router-link>
                    <app-back-button right/>
                    {{ $t('Confirmed') }}: {{ booking.confirmed_at | formatDateTime }}
                </div>
                <div v-else>
                    <app-button native-type="button" type="success" @click="confirmBooking(booking.id, true)" small>
                        {{ $t('Confirm') }}
                    </app-button>
                    <router-link :to="{name: 'booking.edit', params: {booking_id: booking.id}}" class="btn btn-primary btn-sm float-right text-white">
                        {{ $t('Edit') }}
                    </router-link>
                    <app-back-button right/>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col card p-3 mx-2">
                <div class="d-flex justify-content-between">
                    <h4>{{ $t('Vehicle') }}</h4>
                    <router-link :to="{name: 'vehicles.show', params: {vehicle_id: booking.vehicle_id}}" class="btn btn-info btn-sm text-white">
                        {{ $t('Vehicle') }}
                    </router-link>
                </div>
                <h5 class="custom-gray-input-bg">{{ getVehicleName(booking.vehicle_id) }}</h5>
                <div class="form-group mb-1">
                    <label class="mb-0">{{ $t('Start date') }} & {{ $t('Return date') }}</label>
                    <p class="custom-gray-input-bg">{{ booking.start_time | formatDateTime }} - {{ booking.end_time | formatDateTime }}</p>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Pick up from') }}</label>
                            <p class="custom-gray-input-bg">{{ locations.find(c => c.id === booking.pickup_location_id).name[locale] }}</p>
                            <template v-if="booking.pickup_location_id === otherLocationId">
                                <p class="custom-gray-input-bg">{{ booking.pickup_location_custom }}</p>
                            </template>
                        </div>
                    </div>
                    <div class="col">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Return to') }}</label>
                            <p class="custom-gray-input-bg">{{ locations.find(c => c.id === booking.return_location_id).name[locale] }}</p>
                            <template v-if="booking.return_location_id === otherLocationId">
                                <p class="custom-gray-input-bg">{{ booking.return_location_custom }}</p>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Internal notes') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.notes }}</p>
                        </div>
                    </div>
                    <div v-if="booking.travel_out" class="col">
                        <br>
                        <template>
                            <span class="mdi mdi-18px mdi-alert-outline text-danger"/> {{ $t('Travel out of Estonia') }}: <b>{{ booking.travel_out }}</b>
                        </template>
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col-md-6 col-sm-12">
                        <div class="accordion" id="ad_eqs">
                            <div class="card shadow-none">
                                <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#additional_equipment">
                                    <a class="card-link font-weight-normal pl-1">
                                        {{ $t('Additional eq') }}
                                        <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                    </a>
                                </div>
                                <div id="additional_equipment" class="collapse show pt-3" aria-labelledby="headingL-varustus" data-parent="#ad_eqs">
                                    <app-loader-gif v-if="loading"/>
                                    <template v-else>
                                        <app-custom-checkbox v-for="(eq, j) in additions"
                                                             :id="`eq_${j}`"
                                                             :name="`eq_${j}`"
                                                             :key="j"
                                                             :value="booking.additional_eq.map(e => e.id).includes(eq.id)"
                                                             disabled>
                                            {{ $t(eq.name[locale]) }}
                                        </app-custom-checkbox>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="accordion" id="services_acc">
                            <div class="card shadow-none">
                                <div class="card-header pointer accordeon-border" data-toggle="collapse" href="#services">
                                    <a class="card-link font-weight-normal pl-1">
                                        {{ $t('Services') }}
                                        <fa-icon :icon="['fas', 'angle-down']" pull="right"/>
                                    </a>
                                </div>
                                <div id="services" class="collapse show" aria-labelledby="headingL-varustus" data-parent="#services_acc">
                                    <div class="card-body">
                                        <app-loader-gif v-if="loading"/>
                                        <template v-if="!loading && booking.services.length">
                                            <p class="mb-1" v-for="(serv, s) in booking.services" :key="`s_${s}`">
                                                {{ serv.name[locale] }}, <strong :key="`se_${s}`">{{ (serv.price / 100).toFixed(2) }}€</strong><br :key="`ser_${s}`"/>
                                            </p>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col card p-3 mx-2">
                <h4>{{ $t('Rentee') }}</h4>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('First name') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.fname }}</p>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Last name') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.lname }}</p>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Phone') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.phone }}</p>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Email') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.email }}</p>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Pick up & return info') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.comments }}</p>
                        </div>
                    </div>
                    <div class="col-lg-6 col-md-6">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Language') }}</label>
                            <p class="custom-gray-input-bg">{{ $t(langs.find(l => l.key === booking.lang).name) }}</p>
                        </div>
                    </div>
                </div>
                <hr>
                <div class="row">
                    <div class="col">
                        <h4>{{ $t('Rental prices') }}</h4>
                        <div class="row">
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Days') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.days }}</p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Day price') }}</label>
                                    <p class="custom-gray-input-bg">
                                        {{ booking.prices.avg_price }} €
                                    </p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Eqipment price') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.eq_total }} €</p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Services') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.service_total }} €</p>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Extra hours') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.over_hrs }}</p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Extra hour price') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.hr_price }} €</p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Bond') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.bond }} €</p>
                                </div>
                            </div>
                            <div class="col">
                                <div class="form-group mb-1">
                                    <label class="mb-0">{{ $t('Total price') }}</label>
                                    <p class="custom-gray-input-bg">{{ booking.prices.total }} €</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="booking.early_return_location_id || booking.early_return_at" class="row">
                    <div class="col">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Early return date & time') }}</label>
                            <p class="custom-gray-input-bg">{{ booking.early_return_at | formatDateTime }}</p>
                        </div>
                    </div>
                    <div class="col">
                        <div class="form-group mb-1">
                            <label class="mb-0">{{ $t('Early return to') }}</label>
                            <p class="custom-gray-input-bg">{{ locations.find(c => c.id === booking.early_return_location_id).name[locale] }}</p>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <div v-if="booking.promo">
                            <div class="form-group mb-1">
                                <label class="mb-0">{{ $t('Promocode') }}</label>
                                <p class="custom-gray-input-bg">"{{ booking.promo.code }}" <small>-{{ booking.promo.discount_percent }}%</small></p>
                            </div>
                        </div>
                        <div v-else-if="booking.client_id !== null">
                            <div class="form-group mb-1">
                                <label class="mb-0">{{ $t('Client') }}</label>
                                <p class="custom-gray-input-bg">{{ booking.client.name }} <small>-{{ booking.client.discount_percent }}%</small></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <app-loader-svg v-else/>
</template>

<script>
import BookingService from '@services/booking.service';
import {mapGetters} from 'vuex';
import AdditionService from '@services/addition.service';
import LocationService from '@services/location.service';
import VehicleService from '@services/vehicle.service';
import chunk from 'lodash/chunk';

export default {
    data() {
        return {
            booking: {},
            vehicles: [],
            additions: [],
            locations: [],
            loading: false,
        };
    },
    watch: {
        locale() {
            this.getBooking(this.$route.params.booking_id);
        }
    },
    computed: {
        ...mapGetters(['locale', 'langs', 'otherLocationId'])
    },
    methods: {
        confirmBooking(bookingId, confirm) {
            let msg = this.$t('Unconfirm');
            if (confirm) {
                msg = this.$t('Confirm');
            }
            if (!window.confirm(`${msg}?`)) {
                return false;
            }
            BookingService.confirmBooking(bookingId).then(res => {
                this.booking = res;
                this.setPrices(res.prices);
                this.$toastr.s(this.$t('Updated'));
            });
        },
        getVehicleName(id) {
            const vehicle = this.vehicles.some(c => c.id === id) ? this.vehicles.find(c => c.id === id) : null;
            if (vehicle) {
                return `${vehicle.make.name} ${vehicle.model.name} ${vehicle.slug.slice(-1)}, ${vehicle.reg_no}`;
            }
            return vehicle;
        },
        getVehicles() {
            VehicleService.noEmpties().then(res => this.vehicles = res);
        },
        getLocations() {
            LocationService.locations().then(res => {
                this.locations = res;
                this.getAdditions();
            });
        },
        getBooking(bookingId) {
            this.loading = true;
            BookingService.booking(bookingId).then(res => {
                this.booking = res;
                this.setPrices(res.prices);
                this.getLocations();
            });
        },
        setPrices(prices) {
            this.booking.prices.eq_total = (prices.eq_total / 100).toFixed(2);
            this.booking.prices.service_total = prices.service_total / 100;
            this.booking.prices.avg_price = (prices.avg_price / 100).toFixed(2);
            this.booking.prices.days = prices.days;
            this.booking.prices.bond = prices.bond / 100;
            this.booking.prices.total = (prices.total / 100).toFixed(2);
            this.booking.prices.added_price = (prices.added_price / 100).toFixed(2);
            this.booking.prices.hr_price = (prices.hr_price / 100).toFixed(2);
            this.booking.prices.over_hrs = prices.over_hrs;
        },
        getAdditions() {
            AdditionService.additions().then(res => {
                this.additions = res;
                this.loading = false;
            });
        },
    },
    created() {
        this.getBooking(this.$route.params.booking_id);
        this.getVehicles();
    }
};
</script>
